/**
 * Crew name — first-run naming for Your Crew
 *
 * Persists under pulse_preferences.card_type_preferences.meta (same jsonb
 * nest as crew_member_draft — no migration):
 * - crew_name: user-chosen name ("Ada's Crew", "Night Shift", …)
 * - crew_name_onboarded: true once the user saved or skipped the first-run step
 *
 * Display rule: saved name → "{Display name}'s Crew" → CREW_YOUR_CREW_PAGE_TITLE.
 * Isomorphic; mobile mirrors in mobile/lib/crew when the native screen ships.
 */

export const CREW_NAME_META_KEY = 'crew_name' as const
export const CREW_NAME_ONBOARDED_META_KEY = 'crew_name_onboarded' as const
export const CREW_NAME_MAX = 40

/** First-run card copy. */
export const CREW_NAME_ONBOARDING_TITLE = 'Name your crew'
export const CREW_NAME_ONBOARDING_BODY =
  'This is what your crew is called across Losi. You can change it anytime.'
export const CREW_NAME_INPUT_A11Y = 'Crew name'
export const CREW_NAME_SAVE_LABEL = 'Save name'
export const CREW_NAME_SKIP_LABEL = 'Keep default'
export const CREW_NAME_RENAME_A11Y = 'Rename crew'

type ProfileNameSource = {
  display_name?: string | null
  username?: string | null
} | null | undefined

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

/** Trim, collapse whitespace, strip control chars, cap length. Empty → null. */
export function normalizeCrewName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const cleaned = raw
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, CREW_NAME_MAX)
    .trim()
  return cleaned || null
}

/** First word-ish of the user's name, used for the default possessive. */
function profileFirstName(profile: ProfileNameSource): string | null {
  const source =
    normalizeCrewName(profile?.display_name) || normalizeCrewName(profile?.username)
  if (!source) return null
  const first = source.split(' ')[0] || source
  return first.slice(0, CREW_NAME_MAX - "'s Crew".length) || null
}

/** Default "{Name}'s Crew" (names ending in s get "{Name}' Crew"). */
export function defaultCrewName(profile: ProfileNameSource): string | null {
  const name = profileFirstName(profile)
  if (!name) return null
  return /s$/i.test(name) ? `${name}' Crew` : `${name}'s Crew`
}

export type CrewIdentity = {
  crewName: string | null
  onboarded: boolean
}

export function extractCrewIdentity(cardTypePreferences: unknown): CrewIdentity {
  const meta = isRecord(cardTypePreferences) ? cardTypePreferences.meta : null
  if (!isRecord(meta)) return { crewName: null, onboarded: false }
  return {
    crewName: normalizeCrewName(meta[CREW_NAME_META_KEY]),
    onboarded: meta[CREW_NAME_ONBOARDED_META_KEY] === true
  }
}

/**
 * Merge crew name / onboarded flag without dropping sibling toggles or meta.
 * crewName: string sets, null clears, undefined leaves unchanged.
 */
export function mergeCrewIdentity(
  existing: unknown,
  patch: { crewName?: string | null; onboarded?: boolean }
): Record<string, unknown> {
  const base: Record<string, unknown> = isRecord(existing) ? { ...existing } : {}
  const meta: Record<string, unknown> = isRecord(base.meta) ? { ...base.meta } : {}

  if (patch.crewName !== undefined) {
    const normalized = normalizeCrewName(patch.crewName)
    if (normalized) meta[CREW_NAME_META_KEY] = normalized
    else delete meta[CREW_NAME_META_KEY]
  }
  if (patch.onboarded !== undefined) {
    meta[CREW_NAME_ONBOARDED_META_KEY] = patch.onboarded === true
  }

  base.meta = meta
  return base
}

/** Name to display: saved → default from profile → fallback title. */
export function resolveCrewDisplayName(
  identity: Pick<CrewIdentity, 'crewName'> | null | undefined,
  profile: ProfileNameSource,
  fallback: string
): string {
  return identity?.crewName || defaultCrewName(profile) || fallback
}

/** Show the first-run naming step until the user saved or skipped once. */
export function shouldShowCrewNameOnboarding(
  identity: CrewIdentity | null | undefined
): boolean {
  return Boolean(identity) && !identity?.onboarded
}
